import { colors } from '@globals/themes/defaultTheme'
import { FeatureEnum, LicenseFeatures } from '@kudos/http-client'
import Box from '@mui/material/Box'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Typography from '@mui/material/Typography'
import { T } from '@transifex/react'
import { ReactNode, useMemo } from 'react'

const featureLabelMapping: Partial<Record<FeatureEnum, ReactNode>> = {
  [FeatureEnum.NOMINATIONS]: <T _str="Nominations" />,
  [FeatureEnum.INCENTIVES]: <T _str="Incentives" />,
  [FeatureEnum.ENHANCED_KTV]: <T _str="KTV audience segmentation" />,
  [FeatureEnum.RECOGNITION]: <T _str="Recognition" />,
  [FeatureEnum.REFINEMENT]: <T _str="Recognition AI" />,
  [FeatureEnum.CUSTOM_REWARDS]: <T _str="Custom rewards" />,
}

type DefaultFeature = { key: string; label: ReactNode }

const defaultEnabledFeatures: DefaultFeature[] = [
  { key: 'celebrationsAndCards', label: <T _str="Celebrations & cards" /> },
  { key: 'awards', label: <T _str="Awards" /> },
  { key: 'announcements', label: <T _str="Announcements" /> },
  { key: 'ktv', label: <T _str="KTV" /> },
]

type LicensingFeaturesProps = {
  licenseFeatures: LicenseFeatures
}

const LicensingFeatures: React.FC<LicensingFeaturesProps> = ({ licenseFeatures }) => {
  const { enabledFeatures, disabledFeatures } = useMemo(() => {
    // NOTE: Features without labels are excluded (see SYN-4795).
    // To add a feature, add its label.
    const licenseFeatureEntries = Object.entries(licenseFeatures)
    const licenseEnabledFeatures = licenseFeatureEntries.filter(([_, enabled]) => enabled)
    const licenseDisabledFeatures = licenseFeatureEntries.filter(([_, enabled]) => !enabled)

    const enabledFeatures = [
      ...defaultEnabledFeatures.map(feature => ({ ...feature, isEnabled: true })),
      ...licenseEnabledFeatures
        .filter(([key]) => key in featureLabelMapping)
        .map(([key, isEnabled]) => ({
          key,
          label: featureLabelMapping[key as FeatureEnum],
          isEnabled,
        })),
    ]

    const disabledFeatures = licenseDisabledFeatures
      .filter(([key]) => key in featureLabelMapping)
      .map(([key, isEnabled]) => ({
        key,
        label: featureLabelMapping[key as FeatureEnum],
        isEnabled,
      }))

    return { enabledFeatures, disabledFeatures }
  }, [licenseFeatures])

  const renderFeatureList = (
    features: Array<{ key: string; label: ReactNode; isEnabled: boolean }>,
  ) => (
    <List
      dense
      sx={{
        listStyleType: 'disc',
        pl: 2,
        '& .MuiListItem-root': {
          display: 'list-item',
        },
      }}>
      {features.map(({ key, label, isEnabled }) => (
        <ListItem
          key={key}
          disablePadding
          sx={{ color: isEnabled ? colors.grayMedium : colors.bluePrimary }}>
          <ListItemText
            primary={label}
            slotProps={{
              primary: {
                variant: 'body1',
              },
            }}
            sx={{
              '& .MuiListItemText-primary': {
                color: 'inherit',
              },
            }}
          />
        </ListItem>
      ))}
    </List>
  )

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 2,
        width: '100%',
      }}>
      <Box>
        <Typography color={colors.grayMedium} variant="caption">
          <T _str="Your current plan:" />
        </Typography>
        {renderFeatureList(enabledFeatures)}
      </Box>
      <Box>
        <Typography color={colors.bluePrimary} variant="caption">
          <T _str="Upgrade to unlock:" />
        </Typography>
        {renderFeatureList(disabledFeatures)}
      </Box>
    </Box>
  )
}

export default LicensingFeatures
